/**
 * UserMenu — the signed-in account menu. An avatar trigger opens a dropdown
 * showing the user's email, current organization (switchable when the user
 * belongs to more than one) and a log-out action.
 */

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@itoresearch/design-system";

import { useAppAuth } from "../../hooks/use-auth";
import { useMe } from "../../hooks/use-me";
import { useSwitchOrganization } from "../../hooks/use-switch-organization";

export function UserMenu() {
  const { user, signOut } = useAppAuth();
  const me = useMe();
  const switchOrg = useSwitchOrganization();

  const organizations = me.data?.organizations ?? [];
  // The server's active org may (transiently) not be in the user's org list;
  // never fall back to a list entry — showing a falsely-"active" org would
  // suppress switching to it. Null means "no active org", which renders an
  // unselected radio group.
  const active =
    organizations.find((o) => o.id === me.data?.activeOrganizationId) ?? null;
  const displayName = me.data?.user.firstName ?? user?.email ?? "Account";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="rounded-full"
            aria-label="Account menu"
          >
            <Avatar size="sm">
              {user?.profilePictureUrl && (
                <AvatarImage src={user.profilePictureUrl} alt="" />
              )}
              <AvatarFallback>
                {(me.data?.user.firstName ?? user?.email ?? "?")
                  .slice(0, 1)
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>{displayName}</DropdownMenuLabel>
        {user?.email && (
          <p className="px-1.5 pb-1 text-xs text-muted-foreground">
            {user.email}
          </p>
        )}
        <DropdownMenuSeparator />
        {organizations.length > 1 ? (
          <>
            <DropdownMenuLabel>Organization</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={active?.id ?? ""}
              disabled={switchOrg.isPending}
              onValueChange={(id) => {
                if (id && id !== active?.id) switchOrg.mutate(id);
              }}
            >
              {organizations.map((org) => (
                <DropdownMenuRadioItem key={org.id} value={org.id}>
                  {org.name}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            {switchOrg.isPending && (
              <p className="px-1.5 pb-1 text-xs text-muted-foreground">
                Switching…
              </p>
            )}
            {switchOrg.isError && (
              <p className="px-1.5 pb-1 text-xs text-destructive">
                Switch failed — try again
              </p>
            )}
          </>
        ) : organizations.length === 1 ? (
          <div className="px-1.5 pb-1">
            <p className="text-xs font-medium text-muted-foreground">
              Organization
            </p>
            <p className="text-sm">{organizations[0].name}</p>
          </div>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={() => signOut()}>
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
